import { createContext, useEffect, useRef, useState } from 'react';
import jwt_decode from 'jwt-decode';
import * as queryString from 'query-string';
import { useHistory } from 'react-router';
import { HOME_PATH, SIGN_IN_PATH } from 'routes/constants.routes';
import { AUTH_API_URL, AuthService } from 'lib/api';

import { useResErrors, useRoutes } from 'hooks';
import { deepCopyObject, LocalStorageService } from 'lib/utils';
import { useModalbar } from 'modules/Modalbar';
import { useTranslation } from 'react-i18next';
import { LoaderBackdrop } from 'asterX-UI/Loaders';
import { useMediaQuery } from '@material-ui/core';

const initialState = {
  token: '',
  data: {},
};

export const AuthStoreContext = createContext({
  authStore: {},
  authStoreFunc: {},
});
const AuthStoreProvider = (props) => {
  let currentPathname;
  let currentSearch;
  const { location } = props;
  const { t } = useTranslation();
  const { goTo, setLastVisit, lastVisit } = useRoutes();
  const { standardCheck } = useResErrors();
  const { callModalbar } = useModalbar();
  const _isMounted = useRef(true);
  const history = useHistory();
  const [appBar, setAppBar] = useState(true);
  const [user, setUser] = useState(initialState);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);

  useEffect(() => {
    setInitLoading(true);
    const values = queryString.parse(location?.search);
    if (values && values.token) {
      setTokenFromQuery(values.token);
      setAppBar(false);
    }
    if (user.token) {
      setTokenFromQuery(user.token);
    } else {
      setTokenFromLS();
    }
    return () => {
      _isMounted.current = false;
    };
  }, []);

  const logout = () => setDataToStorage(null, true);

  const isAdminProject = (user) => !!user?.roleList?.some((el) => el?.project?.code === 'UA' && el?.code === 'ADMIN');

  const setDataToStorage = async (token = '', isReset = false) => {
    AuthService.setToken(token);
    if (isReset) {
      setUser(deepCopyObject(initialState));
      setIsLoggedIn(false);
      return;
    }
    setLoading(true);
    return await AuthService.getUserByToken().then((res) =>
      standardCheck(
        res,
        () => {
          setUser((prevState) => ({ ...prevState, token, data: res.data }));
          setIsLoggedIn(!!token && !!res?.data?.id);
          setLoading(false);
        },
        {
          onFail: () => {
            setIsLoggedIn(false);
            setLoading(false);
            AuthService.setToken();
            goTo(SIGN_IN_PATH);
          },
        }
      )
    );
  };

  const setTokenFromQuery = async (token) => {
    if (!token) {
      setInitLoading(false);
      return false;
    }
    try {
      const decoded = jwt_decode(token);
      if (decoded) {
        await setDataToStorage(token).then((res) => {
          setInitLoading(false);
          goTo(lastVisit ? (lastVisit !== SIGN_IN_PATH ? lastVisit : HOME_PATH) : HOME_PATH);
        });

        return true;
      } else {
        await setDataToStorage(null, true);
        setInitLoading(false);
        return false;
      }
    } catch (e) {
      return false;
    }
  };

  const setTokenFromLS = () => setTokenFromQuery(LocalStorageService.getAccessToken());

  const login = async (userData) => {
    setLoading(true);
    await AuthService.login(userData).then((res) =>
      standardCheck(
        res,
        async () => {
          setLoading(false);
          const { token } = res.data;
          const decoded = jwt_decode(token);
          if (!!token && !!decoded) {
            await setDataToStorage(token).then((res) => {
              console.log('res', res);
              goTo(lastVisit ? (lastVisit !== SIGN_IN_PATH ? lastVisit : HOME_PATH) : HOME_PATH);
            });
          } else {
            callModalbar(t('errors.NotAccess_SignIn.title'), t('errors.NotAccess_SignIn.body'));
          }
        },
        {
          onErrCustom: () => {
            setLoading(false);
            callModalbar(t('errors.Unauthorized_SignIn.title'), t('errors.Unauthorized_SignIn.body'));
          },
        }
      )
    );
  };

  useEffect(() => {
    setLastVisit(location?.pathname);
  }, [location?.pathname]);

  useEffect(() => {
    history.listen((newLocation, action) => {
      if (action === 'PUSH') {
        if (newLocation.pathname !== currentPathname || newLocation.search !== currentSearch) {
          // Save new location
          currentPathname = newLocation.pathname;
          currentSearch = newLocation.search;

          // Clone location object and push it to history
          history.push({
            pathname: newLocation.pathname,
            search: newLocation.search,
          });
        }
      } else {
        // Send user back if they try to navigate back
        history.go(1);
      }
    });
    return () => {
      _isMounted.current = false;
    };
  }, [history]);

  const authStore = {
    appBar,
    user,
    isLoggedIn,
    loading,
  };

  const authStoreFunc = {
    login,
    logout,
    setTokenFromQuery,
    setTokenFromLS,
  };
  return (
    <AuthStoreContext.Provider value={{ authStore, authStoreFunc }}>{initLoading ? <LoaderBackdrop loading={initLoading} /> : <>{props.children}</>}</AuthStoreContext.Provider>
  );
};
export default AuthStoreProvider;
